/**
 * ActiveSessionsPage — live session monitor across all tenants/tiers.
 *
 * Architecture:
 *   - SessionsToolbar     → top bar (refresh, client/status filters)
 *   - SessionsActivityLog → per-day activity log
 *   - ActiveSessionsBlocks → StatCard, DistributionCard, ClientDetailPanel, StatusBadge
 *   - ERPAdvancedGrid      → sortable/paginated sessions table
 */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Activity, Wifi, WifiOff, CircleDot, Building2, Server } from 'lucide-react';
import ERPAdvancedGrid from './erp/ERPAdvancedGrid';
import { erpToast } from './erp/ERPToast';
import { StatCard, DistributionCard, ClientDetailPanel } from './ActiveSessionsBlocks';
import { SessionsToolbar }      from './sessions/SessionsToolbar';
import { SessionsActivityLog }  from './sessions/SessionsActivityLog';
import { API, apiFetch } from '../lib/api';

const AUTO_REFRESH_MS  = 30000;
const fmt      = (s) => s ? new Date(s).toLocaleString() : '—';


// ─────────────────────────────────────────────────────────────────────────────
export default function ActiveSessionsPage() {
  // ── Data state ──────────────────────────────────────────────────────────────
  const [data,            setData]           = useState({ sessions: [], stats: null });
  const [loading,         setLoading]        = useState(true);
  const [error,           setError]          = useState(null);
  const [lastFetched,     setLastFetched]    = useState(null);
  const [selectedSession, setSelectedSession] = useState(null);
  const [clientDetail,    setClientDetail]   = useState(null);
  const [loadingDetail,   setLoadingDetail]  = useState(false);
  const [activity,        setActivity]       = useState({ events: [], totals: {} });
  const [activityDate,    setActivityDate]   = useState(() => new Date().toISOString().slice(0, 10));
  const [activityLoading, setActivityLoading] = useState(false);

  // ── Grid/filter state ────────────────────────────────────────────────────────
  const [page,          setPage]         = useState(1);
  const [pageSize,      setPageSize]     = useState(20);
  const [sortField,     setSortField]    = useState('LastSeenAt');
  const [sortDir,       setSortDir]      = useState('desc');
  const [filters,       setFilters]      = useState({});
  const [statusFilter,  setStatusFilter] = useState('ALL');
  const [clientFilter,  setClientFilter] = useState('ALL');
  const [clientOptions, setClientOptions] = useState([]);

  // ── Loaders ──────────────────────────────────────────────────────────────────
  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({ sinceHours: '24' });
      if (clientFilter && clientFilter !== 'ALL') qs.set('clientId', clientFilter);
      const res = await apiFetch(`${API}/licensor/active-sessions?${qs}`);
      const d   = await res.json();
      if (!res.ok || d.error) setError(d.error || `HTTP ${res.status}`);
      else { setData(d); setError(null); setLastFetched(new Date()); }
    } catch (e) { setError(e.message); }
    setLoading(false);
  }, [clientFilter]);

  const loadActivity = useCallback(async (date) => {
    setActivityLoading(true);
    try {
      const res = await apiFetch(`${API}/licensor/activity-log?date=${encodeURIComponent(date)}`);
      const d   = await res.json();
      if (res.ok) setActivity(d);
    } catch (_) { /* non-blocking */ }
    setActivityLoading(false);
  }, []);

  // Populate client dropdown from unfiltered 7-day window (refreshes every 60s)
  useEffect(() => {
    let cancelled = false;
    const refresh = async () => {
      try {
        const res = await apiFetch(`${API}/licensor/active-sessions?sinceHours=168`);
        const d   = await res.json();
        if (cancelled || !res.ok) return;
        const map = new Map();
        for (const s of d.sessions || []) {
          if (s.ClientId) map.set(s.ClientId, s.CompanyName || s.ClientId);
        }
        setClientOptions(Array.from(map.entries()).map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name)));
      } catch (_) { /* non-blocking */ }
    };
    refresh();
    const id = setInterval(refresh, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);
  useEffect(() => { loadActivity(activityDate); }, [activityDate, loadActivity]);
  useEffect(() => { const id = setInterval(loadSessions, AUTO_REFRESH_MS); return () => clearInterval(id); }, [loadSessions]);

  // ── Session actions ──────────────────────────────────────────────────────────
  const onForceLogout = async (session) => {
    if (!window.confirm(`Force logout "${session.Username}" (session ${String(session.SessionId).substring(0, 8)})?\n\nTheir next API call will receive 401.`)) return;
    try {
      const res = await apiFetch(`${API}/licensor/sessions/${session.SessionId}/force-logout`, { method: 'POST' });
      const d   = await res.json();
      if (!res.ok || !d.success) erpToast.error(`Force-logout failed: ${d.error || res.status}`);
      else { erpToast.success(`Session for ${session.Username} terminated`); loadSessions(); }
    } catch (e) { erpToast.error(`Force-logout failed: ${e.message}`); }
  };

  const onSelectSession = useCallback(async (session) => {
    setSelectedSession(session);
    setClientDetail(null);
    if (!session.ClientId) return;
    setLoadingDetail(true);
    try {
      const res = await apiFetch(`${API}/licensor/client/${encodeURIComponent(session.ClientId)}`);
      if (res.ok) setClientDetail(await res.json());
    } catch (_) { /* non-blocking */ }
    setLoadingDetail(false);
  }, []);

  // ── Grid columns ─────────────────────────────────────────────────────────────
  const columns = useMemo(() => ([
    { field: 'Status',      header: 'Status',    visible: true, frozen: true },
    { field: 'Username',    header: 'Username',  visible: true  },
    { field: 'FullName',    header: 'Full Name', visible: false },
    { field: 'CompanyName', header: 'Company',   visible: true  },
    { field: 'ClientId',    header: 'Client ID', visible: true  },
    { field: 'LicenseTier', header: 'Tier',      visible: true  },
    { field: 'Industry',    header: 'Industry',  visible: true  },
    { field: 'Role',        header: 'Role',      visible: true  },
    { field: 'IpAddress',   header: 'IP',        visible: true  },
    { field: 'LoginAt',     header: 'Login',     visible: true  },
    { field: 'LastSeenAt',  header: 'Last Seen', visible: true  },
    { field: 'ageMinutes',  header: 'Age (min)', visible: false },
    { field: 'Email',       header: 'Email',     visible: false },
  ]), []);

  // ── Filter + sort ─────────────────────────────────────────────────────────────
  const filteredSorted = useMemo(() => {
    let r = (data.sessions || []).map(s => ({
      ...s,
      LoginAt:     fmt(s.LoginAt),
      LastSeenAt:  fmt(s.LastSeenAt),
      CompanyName: s.CompanyName || '—',
      ClientId:    s.ClientId   || '—',
      LicenseTier: s.LicenseTier || '—',
      Industry:    s.Industry   || '—',
    }));
    if (statusFilter !== 'ALL') r = r.filter(s => s.Status === statusFilter);
    const af = Object.entries(filters).filter(([, v]) => v != null && String(v).trim() !== '');
    if (af.length) r = r.filter(row => af.every(([k, v]) => String(row[k] ?? '').toLowerCase().includes(String(v).toLowerCase())));
    if (sortField) {
      r = [...r].sort((a, b) => {
        const av = a[sortField], bv = b[sortField];
        if (av == null && bv == null) return 0;
        if (av == null) return 1; if (bv == null) return -1;
        if (typeof av === 'number' && typeof bv === 'number') return sortDir === 'asc' ? av - bv : bv - av;
        return sortDir === 'asc' ? String(av).localeCompare(String(bv)) : String(bv).localeCompare(String(av));
      });
    }
    return r;
  }, [data.sessions, filters, sortField, sortDir, statusFilter]);

  const paginated = useMemo(() => filteredSorted.slice((page - 1) * pageSize, page * pageSize), [filteredSorted, page, pageSize]);
  const findRaw   = (row) => (data.sessions || []).find(s => s.SessionId === row.SessionId) || row;

  // Right-click → select session
  const ctxRef = useRef(null);
  ctxRef.current = { paginated };
  useEffect(() => {
    const handler = (e) => {
      const tr = e.target.closest('tr[data-testid^="sessions-grid-row-"]');
      if (!tr) return;
      e.preventDefault();
      const row = ctxRef.current.paginated.find(s => String(s.SessionId) === tr.getAttribute('data-testid').replace('sessions-grid-row-', ''));
      if (row) onSelectSession(findRaw(row));
    };
    document.addEventListener('contextmenu', handler);
    return () => document.removeEventListener('contextmenu', handler);
  // eslint-disable-next-line
  }, [onSelectSession]);

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div data-testid="active-sessions-page" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

      <SessionsToolbar
        loading={loading} lastFetched={lastFetched} onRefresh={loadSessions}
        clientFilter={clientFilter} clientOptions={clientOptions} onClientChange={(v) => { setClientFilter(v); setPage(1); }}
        statusFilter={statusFilter} onStatusChange={(v) => { setStatusFilter(v); setPage(1); }}
      />

      {data.stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          <StatCard testid="stat-online"  label="Online"      value={data.stats.online}   icon={<Wifi size={16} />}       color="#10b981" />
          <StatCard testid="stat-idle"    label="Idle"        value={data.stats.idle}     icon={<CircleDot size={16} />}  color="#f59e0b" />
          <StatCard testid="stat-offline" label="Offline"     value={data.stats.offline}  icon={<WifiOff size={16} />}    color="#94a3b8" />
          <StatCard testid="stat-total"   label="Total (24h)" value={data.stats.total}    icon={<Activity size={16} />}   color="#3b82f6" />
        </div>
      )}

      {data.stats && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <DistributionCard title="By Tier"     data={data.stats.byTier}     icon={<Server size={12} />}    testid="dist-by-tier"     />
          <DistributionCard title="By Industry" data={data.stats.byIndustry} icon={<Building2 size={12} />} testid="dist-by-industry" />
        </div>
      )}

      {error && (
        <div style={{ padding: 10, background: '#fef2f2', color: '#b91c1c', borderRadius: 6, fontSize: 12 }}>
          {error}
        </div>
      )}

      <ERPAdvancedGrid
        title={`Sessions (${filteredSorted.length})`}
        columns={columns} data={paginated} total={filteredSorted.length}
        idField="SessionId" nameField="Username"
        page={page} pageSize={pageSize} onPageChange={setPage} onPageSizeChange={setPageSize}
        sortField={sortField} sortDir={sortDir}
        onSort={(f) => { if (sortField === f) setSortDir(d => d === 'asc' ? 'desc' : 'asc'); else { setSortField(f); setSortDir('asc'); } }}
        filters={filters} onFilterChange={(f) => { setFilters(f); setPage(1); }}
        loading={loading} showAddNew={false}
        onEdit={(row) => onSelectSession(findRaw(row))}
        onPreview={(row) => onSelectSession(findRaw(row))}
        testId="sessions-grid"
      />

      {selectedSession && (
        <ClientDetailPanel
          session={selectedSession} detail={clientDetail} loading={loadingDetail}
          onClose={() => { setSelectedSession(null); setClientDetail(null); }}
          onForceLogout={onForceLogout}
        />
      )}

      <SessionsActivityLog
        activity={activity} activityDate={activityDate} activityLoading={activityLoading}
        onDateChange={setActivityDate} onRefresh={() => loadActivity(activityDate)}
      />

      <style>{`.spinning { animation: spin 1s linear infinite; } @keyframes spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
